import type { Round } from '@h2h/shared';
import { ChevronLeft, ChevronRight } from '../../../components/ui/icons';
import { cn } from '../../../lib/cn';
import { parseStageLabel, stagePhaseTone, STAGE_PHASE_PILL, stageSubtitle } from './helpers';

/** Stage browser — same ← label → arrow nav as the football fixtures round selector. */
export function StageChipPager({
  rounds,
  selectedId,
  onSelect
}: {
  rounds: Round[];
  selectedId: string | null;
  currentRoundId?: string | null;
  onSelect: (roundId: string) => void;
}) {
  const activeIdx = rounds.findIndex((r) => r.roundId === selectedId);
  const idx = activeIdx >= 0 ? activeIdx : 0;
  const selected = rounds[idx];
  const canPrev = idx > 0;
  const canNext = idx < rounds.length - 1;

  const { heading, route } = parseStageLabel(selected?.label ?? 'This stage');
  const phase = stagePhaseTone(selected?.status);
  const pill = STAGE_PHASE_PILL[phase];

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-3">
      <button
        type="button"
        disabled={!canPrev}
        onClick={() => canPrev && onSelect(rounds[idx - 1].roundId)}
        className={cn(
          'tap grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F2F2F5] transition-opacity',
          !canPrev && 'opacity-25'
        )}
      >
        <ChevronLeft width={20} height={20} strokeWidth={2.4} className="text-ink2" />
      </button>

      <div className="min-w-0 flex-1 text-center">
        <div className="truncate text-[15px] font-extrabold tracking-[-0.01em] text-ink">
          {heading}
          {route ? <span className="font-semibold text-ink3"> · {route}</span> : null}
        </div>
        {selected && (
          <div className="mt-[2px] text-[12px] font-semibold text-ink2">
            {stageSubtitle(selected)}
            <span className={cn('ml-[6px] rounded-full px-[7px] py-[2px] text-[10px] font-bold', pill.className)}>
              {pill.label}
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={!canNext}
        onClick={() => canNext && onSelect(rounds[idx + 1].roundId)}
        className={cn(
          'tap grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F2F2F5] transition-opacity',
          !canNext && 'opacity-25'
        )}
      >
        <ChevronRight width={20} height={20} strokeWidth={2.4} className="text-ink2" />
      </button>
    </div>
  );
}
